import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import type { PhotoFormat } from '../../core/models/contact-photo.model';
import { ContactPhotoService } from '../../core/services/contact-photo.service';
import { ContactPhotoUploadComponent } from './contact-photo-upload.component';

/**
 * Contact Photo Component
 *
 * Displays contact photos with fallback to default avatar
 * Follows Angular best practices with signals
 */
@Component({
  selector: 'app-contact-photo',
  standalone: true,
  imports: [CommonModule, ContactPhotoUploadComponent],
  template: `
    <div class="relative inline-block m-2">
      @if (photoUrl() && !imageError()) {
      <img [src]="photoUrl()!" [alt]="altText()" 
           class="rounded-lg object-cover shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5"
           [class.w-20.h-20]="size() === 'small'"
           [class.w-[140px].h-[140px]]="size() === 'medium'"
           [class.w-[200px].h-[200px]]="size() === 'large'"
           (error)="onImageError()" />
      } @else {
      <div class="flex flex-col items-center justify-center bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white relative overflow-hidden rounded-lg shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5"
           [class.w-20.h-20]="size() === 'small'"
           [class.w-[140px].h-[140px]]="size() === 'medium'"
           [class.w-[200px].h-[200px]]="size() === 'large'">
        
        <!-- Shimmer effect -->
        <div class="absolute inset-[-50%] w-[200%] h-[200%] bg-gradient-to-tr from-transparent via-white/10 to-transparent animate-shimmer"></div>
        
        <div class="z-10 drop-shadow-md"
             [class.text-3xl]="size() === 'small'"
             [class.text-[48px]]="size() === 'medium'"
             [class.text-[56px]]="size() === 'large'">👤</div>
        
        @if (showFallbackText()) {
        <span class="z-10 text-center font-medium drop-shadow-sm leading-tight px-2"
              [class.text-[9px].mt-1]="size() === 'small'"
              [class.text-[10px].mt-1]="size() === 'medium'"
              [class.text-[11px].mt-1.5]="size() === 'large'">
          {{ fallbackText() }}
        </span>
        }
      </div>
      } 
      
      @if (loading()) {
      <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white/95 rounded-lg p-3 shadow-md z-20">
        <div class="w-10 h-10 border-[3px] border-black/10 border-t-[#667eea] rounded-full animate-spin"></div>
      </div>
      }

      <!-- Upload UI - only shown if enableUpload is true -->
      @if (enableUpload()) {
      <div class="mt-4 pt-4 border-t border-gray-200">
        <app-contact-photo-upload
          [contactId]="contactId()"
          [hasExistingPhoto]="hasPhoto()"
          [showDeleteButton]="showDeleteButton()"
          (photoUploaded)="onPhotoUploaded()"
          (photoDeleted)="onPhotoDeleted()"
        />
      </div>
      }
    </div>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactPhotoComponent {
  private contactPhotoService = inject(ContactPhotoService);
  private destroyRef = inject(DestroyRef);

  // Inputs using new input() function
  readonly contactId = input.required<number>();
  readonly size = input<'small' | 'medium' | 'large'>('large');
  readonly format = input<PhotoFormat>('jpg');
  readonly showFallbackText = input<boolean>(false);
  readonly fallbackText = input<string>('Kein Foto');
  readonly enableUpload = input<boolean>(false);
  readonly showDeleteButton = input<boolean>(true);

  // Outputs
  readonly photoChanged = output<void>();

  // Internal state
  readonly photoUrl = signal<string | null>(null);
  readonly loading = signal(false);
  readonly imageError = signal(false);
  readonly hasPhoto = signal(false);

  // Computed properties
  readonly altText = computed(() => `Foto von Kontakt ${this.contactId()}`);

  constructor() {
    // Load photo when contactId or format changes
    effect(() => {
      const contactId = this.contactId();
      const format = this.format();

      if (contactId) {
        this.loadPhoto(contactId, format);
      }
    });
  }

  /**
   * Load contact photo
   * Optimized: Uses caching for instant display on subsequent loads
   */
  private loadPhoto(contactId: number, format: PhotoFormat): void {
    this.loadPhotoInternal(contactId, format, false);
  }

  /**
   * Load contact photo with cache busting (for after upload/update)
   */
  private loadPhotoWithCacheBust(contactId: number, format: PhotoFormat): void {
    this.loadPhotoInternal(contactId, format, true);
  }

  /**
   * Internal method to load photo with optional cache busting
   */
  private loadPhotoInternal(contactId: number, format: PhotoFormat, bustCache: boolean): void {
    this.loading.set(true);
    this.imageError.set(false);

    // Directly load photo URL - service handles caching
    // takeUntilDestroyed prevents memory leaks
    this.contactPhotoService
      .getContactPhotoUrl(contactId, format, bustCache)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (url) => {
          if (url) {
            // Photo exists and loaded successfully
            this.photoUrl.set(url);
            this.hasPhoto.set(true);
          } else {
            // Photo doesn't exist (service returns null)
            this.photoUrl.set(null);
            this.hasPhoto.set(false);
          }
        },
        error: (error) => {
          // Handle 404 or other errors gracefully
          console.warn('Failed to load photo:', error);
          this.photoUrl.set(null);
          this.hasPhoto.set(false);
          this.imageError.set(true);
        },
        complete: () => {
          // Reset loading state once
          this.loading.set(false);
        },
      });
  }

  /**
   * Handle image loading errors
   */
  onImageError(): void {
    this.imageError.set(true);
  }

  /**
   * Handle photo uploaded event
   */
  onPhotoUploaded(): void {
    // Photo is already cached by service after upload - just reload from cache
    // This is instant because the uploaded file is already in the cache
    this.loadPhoto(this.contactId(), this.format());
    this.photoChanged.emit();
  }

  /**
   * Handle photo deleted event
   */
  onPhotoDeleted(): void {
    // Clear photo after deletion
    this.photoUrl.set(null);
    this.hasPhoto.set(false);
    this.photoChanged.emit();
  }
}
